import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableFilterDefinition,
  TableAction,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import { firstValueFrom } from 'rxjs';
import { DailyScheduleConfig, DailyScheduleMode, MealBreakMode } from './daily-schedule.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { JourneyConfig } from '../journey/journey.interface';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-daily-schedule',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule, MatCardModule],
  templateUrl: './daily-schedule.component.html',
  styleUrl: './daily-schedule.component.scss',
})
export class DailyScheduleComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  dataSource = new MatTableDataSource<DailyScheduleConfig>([]);
  allSchedules: DailyScheduleConfig[] = [];
  companies: CompaniesRecord[] = [];
  isLoading = true;
  actions: TableAction<DailyScheduleConfig>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<DailyScheduleConfig>[] = [];
  activeSearch = '';
  activeFilters: Record<string, string | number | boolean | null> = {};
  private journeyNamesById = new Map<string, string>();

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.configureTranslations();
        this.allSchedules = this.allSchedules.map((schedule) => this.normalizeSchedule(schedule));
        this.applyTableFilters();
      });
    void this.loadData();
  }

  get schedules(): DailyScheduleConfig[] {
    return this.dataSource.data;
  }

  get activeSchedulesCount(): number {
    return this.schedules.filter((schedule) => schedule.isActive).length;
  }

  get scheduleWithMealBreakCount(): number {
    return this.schedules.filter((schedule) => schedule.mealBreak.enabled).length;
  }

  get overnightSchedulesCount(): number {
    return this.schedules.filter((schedule) => schedule.crossesMidnight).length;
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const [records, companiesResponse, journeys] = await Promise.all([
        firstValueFrom(this.apiService.get<DailyScheduleConfig[]>('labor-config/daily-schedules')),
        this.companies.length
          ? Promise.resolve(this.companies as CompaniesRecord[] | CompaniesRecordsResponse)
          : this.companyDirectory.getAllActive(),
        this.loadJourneysForList(),
      ]);

      this.companies = this.normalizeCompanies(companiesResponse)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
      this.journeyNamesById = new Map(
        journeys.map((journey) => [journey.journeyId, journey.name])
      );
      this.allSchedules = (records || []).map((schedule) => this.normalizeSchedule(schedule));
      this.buildTableFilters();
      this.applyTableFilters();
    } catch (error) {
      console.error('Error loading daily schedules', error);
      this.allSchedules = [];
      this.companies = [];
      this.journeyNamesById.clear();
      this.tableFilters = [];
      this.dataSource.data = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('dailySchedulesCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/daily-schedules/new']);
  }

  async handleEdit(row: DailyScheduleConfig): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/attendance/daily-schedules', row.dailyScheduleId, 'edit'], {
      queryParams: row.companiesRecordId
        ? { companyId: row.companiesRecordId }
        : undefined,
    });
  }

  async handleView(row: DailyScheduleConfig): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/daily-schedules', row.dailyScheduleId], {
      queryParams: row.companiesRecordId
        ? { companyId: row.companiesRecordId }
        : undefined,
    });
  }

  handleDelete(row: DailyScheduleConfig): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('dailySchedulesCatalog.dialog.deleteTitle'),
        message: this.t('dailySchedulesCatalog.dialog.deleteMessage', { schedule: row.name }),
        confirmLabel: this.t('dailySchedulesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`labor-config/daily-schedules/${row.dailyScheduleId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('dailySchedulesCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting daily schedule', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('dailySchedulesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: DailyScheduleConfig[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(
          rows.map((row) => firstValueFrom(this.apiService.delete(`labor-config/daily-schedules/${row.dailyScheduleId}`)))
        );
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting daily schedules', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('dailySchedulesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.activeSearch = search.trim().toLowerCase();
    this.applyTableFilters();
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null>): void {
    this.activeFilters = filters;
    this.applyTableFilters();
  }

  private normalizeSchedule(schedule: Partial<DailyScheduleConfig>): DailyScheduleConfig {
    const mode = (schedule.mode || 'fixed_full') as DailyScheduleMode;
    const mealBreak = schedule.mealBreak || { enabled: false };
    const toleranceRules = schedule.toleranceRules || {};
    const overtimeRules = schedule.overtimeRules || {};
    const blocks = (schedule.blocks || []).map((block, index) => ({
      blockOrder: block.blockOrder || index + 1,
      startTime: block.startTime || '',
      endTime: block.endTime || '',
    }));
    const isActive = schedule.isActive !== false;

    const normalized: DailyScheduleConfig = {
      dailyScheduleId: schedule.dailyScheduleId || crypto.randomUUID(),
      center: schedule.center || '',
      companiesRecordId: schedule.companiesRecordId,
      journeyId: schedule.journeyId,
      name: schedule.name || '',
      mode,
      startTime: schedule.startTime || '',
      endTime: schedule.endTime || '',
      crossesMidnight: !!schedule.crossesMidnight,
      entryWindowStartTime: schedule.entryWindowStartTime || '',
      entryWindowEndTime: schedule.entryWindowEndTime || '',
      minimumWorkMinutes:
        schedule.minimumWorkMinutes !== undefined ? Number(schedule.minimumWorkMinutes) : undefined,
      maximumWorkMinutes:
        schedule.maximumWorkMinutes !== undefined ? Number(schedule.maximumWorkMinutes) : undefined,
      mealBreak: {
        enabled: !!mealBreak.enabled,
        required: !!mealBreak.required,
        mode: (mealBreak.mode || 'fixed') as MealBreakMode,
        minutes: mealBreak.minutes !== undefined ? Number(mealBreak.minutes) : undefined,
        startTime: mealBreak.startTime || '',
        endTime: mealBreak.endTime || '',
        flexibleWindowStartTime: mealBreak.flexibleWindowStartTime || '',
        flexibleWindowEndTime: mealBreak.flexibleWindowEndTime || '',
      },
      toleranceRules: {
        entryToleranceMinutes:
          toleranceRules.entryToleranceMinutes !== undefined ? Number(toleranceRules.entryToleranceMinutes) : undefined,
        earlyExitToleranceMinutes:
          toleranceRules.earlyExitToleranceMinutes !== undefined ? Number(toleranceRules.earlyExitToleranceMinutes) : undefined,
        roundingMarginMinutes:
          toleranceRules.roundingMarginMinutes !== undefined ? Number(toleranceRules.roundingMarginMinutes) : undefined,
      },
      overtimeRules: {
        minimumMinutesBeforeOvertime:
          overtimeRules.minimumMinutesBeforeOvertime !== undefined ? Number(overtimeRules.minimumMinutesBeforeOvertime) : undefined,
        requiresAuthorization: !!overtimeRules.requiresAuthorization,
      },
      blocks,
      isActive,
    };

    normalized.modeLabel = this.getModeLabel(normalized.mode);
    normalized.scheduleSummary = this.getScheduleSummary(normalized);
    normalized.mealBreakSummary = this.getMealBreakSummary(normalized);
    normalized.companyLabel = normalized.companiesRecordId
      ? this.companies.find((company) => company.companiesRecordId === normalized.companiesRecordId)?.name
        || normalized.companiesRecordId
      : '-';
    normalized.journeyLabel = normalized.journeyId
      ? this.journeyNamesById.get(normalized.journeyId) || normalized.journeyId
      : '-';
    normalized.activeLabel = isActive ? this.t('common.status.active') : this.t('common.status.inactive');
    return normalized;
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('dailySchedulesCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'dailySchedules', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'refresh',
        icon: 'refresh',
        tooltip: this.t('common.reload'),
        colorClass: 'neutral',
        auth: { resource: 'dailySchedules', action: 'read' },
        behavior: 'refresh',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('common.select'),
        colorClass: 'info',
        auth: { resource: 'dailySchedules', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: { resource: 'dailySchedules', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: { resource: 'dailySchedules', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('dailySchedulesCatalog.fields.name'), type: 'text', visible: true },
      { def: 'companyLabel', label: this.t('orgUnitsCatalog.fields.company'), type: 'text', visible: true },
      { def: 'journeyLabel', label: this.t('dailySchedulesCatalog.fields.journey'), type: 'text', visible: true },
      { def: 'scheduleSummary', label: this.t('dailySchedulesCatalog.fields.scheduleSummary'), type: 'text', visible: true },
      { def: 'mealBreakSummary', label: this.t('dailySchedulesCatalog.fields.mealBreak'), type: 'text', visible: true },
      { def: 'modeLabel', label: this.t('dailySchedulesCatalog.fields.mode'), type: 'text', visible: true },
      {
        def: 'activeLabel',
        label: this.t('dailySchedulesCatalog.fields.status'),
        type: 'status',
        visible: true,
        statusBadgeMap: {
          [this.t('common.status.active')]: 'badge badge-solid-green',
          [this.t('common.status.inactive')]: 'badge badge-solid-red',
        },
      },
    ];

    this.dataSource.filterPredicate = (schedule, filter) =>
      [schedule.name, schedule.companyLabel, schedule.journeyLabel, schedule.scheduleSummary, schedule.mealBreakSummary, schedule.modeLabel, schedule.activeLabel]
        .join(' ')
        .toLowerCase()
        .includes(filter);
  }

  private buildTableFilters(): void {
    // const modeOptions = Array.from(
    //   new Map(
    //     this.allSchedules.map((schedule) => [
    //       schedule.mode,
    //       {
    //         value: schedule.mode,
    //         label: schedule.modeLabel || this.getModeLabel(schedule.mode),
    //       },
    //     ])
    //   ).values()
    // );

    const statusOptions = [
      { value: 'active', label: this.t('common.status.active') },
      { value: 'inactive', label: this.t('common.status.inactive') },
    ];

    // const mealBreakOptions = [
    //   { value: 'with_meal_break', label: this.t('dailySchedulesCatalog.labels.withMealBreak') },
    //   { value: 'without_meal_break', label: this.t('dailySchedulesCatalog.labels.noMealBreak') },
    // ];

    const companyOptions = this.companies.map((company) => ({
      value: company.companiesRecordId,
      label: company.name,
      searchText: `${company.code || ''} ${company.center || ''}`.trim(),
    }));

    this.tableFilters = [
      {
        id: 'company',
        label: this.t('orgUnitsCatalog.fields.company'),
        icon: 'business',
        value: this.activeFilters['company'] ?? null,
        searchable: true,
        options: companyOptions,
      },
      // {
      //   id: 'mode',
      //   label: this.t('dailySchedulesCatalog.fields.mode'),
      //   icon: 'schedule',
      //   searchable: true,
      //   value: this.activeFilters['mode'] ?? null,
      //   field: 'mode',
      //   options: modeOptions,
      // },
      // {
      //   id: 'mealBreak',
      //   label: this.t('dailySchedulesCatalog.fields.mealBreak'),
      //   icon: 'lunch_dining',
      //   searchable: true,
      //   value: this.activeFilters['mealBreak'] ?? null,
      //   options: mealBreakOptions,
      // },
      {
        id: 'status',
        label: this.t('dailySchedulesCatalog.fields.status'),
        icon: 'verified',
        searchable: true,
        value: this.activeFilters['status'] ?? null,
        options: statusOptions,
      },
    ];
  }

  private applyTableFilters(): void {
    const searchTerm = this.activeSearch;
    const filtered = this.allSchedules.filter((schedule) => {
      const matchesSearch =
        !searchTerm ||
        [schedule.name, schedule.companyLabel, schedule.journeyLabel, schedule.scheduleSummary, schedule.mealBreakSummary, schedule.modeLabel, schedule.activeLabel]
          .join(' ')
          .toLowerCase()
          .includes(searchTerm);

      const matchesMode =
        !this.activeFilters['mode'] || schedule.mode === this.activeFilters['mode'];

      const matchesCompany =
        !this.activeFilters['company'] ||
        schedule.companiesRecordId === this.activeFilters['company'];

      const matchesMealBreak =
        !this.activeFilters['mealBreak'] ||
        (this.activeFilters['mealBreak'] === 'with_meal_break' && schedule.mealBreak.enabled) ||
        (this.activeFilters['mealBreak'] === 'without_meal_break' && !schedule.mealBreak.enabled);

      const matchesStatus =
        !this.activeFilters['status'] ||
        (this.activeFilters['status'] === 'active' && schedule.isActive) ||
        (this.activeFilters['status'] === 'inactive' && !schedule.isActive);

      return matchesSearch && matchesCompany && matchesMode && matchesMealBreak && matchesStatus;
    });

    this.dataSource.data = filtered;
  }

  private getModeLabel(mode: DailyScheduleMode): string {
    return this.t(`dailySchedulesCatalog.modeOptions.${mode}`);
  }

  private getMealBreakModeLabel(mode: MealBreakMode | undefined): string {
    return this.t(`dailySchedulesCatalog.mealBreakModes.${mode || 'fixed'}`);
  }

  private getScheduleSummary(schedule: DailyScheduleConfig): string {
    const range = this.getRangeLabel(schedule);
    const overnight = schedule.crossesMidnight ? this.t('dailySchedulesCatalog.labels.overnight') : '';
    return [range, overnight].filter(Boolean).join(' | ') || this.t('dailySchedulesCatalog.labels.noRange');
  }

  private getRangeLabel(schedule: Partial<DailyScheduleConfig>): string {
    if (schedule.mode === 'entry_window_duration') {
      if (schedule.entryWindowStartTime && schedule.entryWindowEndTime) {
        return this.t('dailySchedulesCatalog.labels.entryWindowSummary', {
          start: schedule.entryWindowStartTime,
          end: schedule.entryWindowEndTime,
        });
      }
      return this.t('dailySchedulesCatalog.labels.entryWindow');
    }

    if (schedule.mode === 'open_shift_with_limits') {
      return this.t('dailySchedulesCatalog.labels.workLimitsSummary', {
        min: Number(schedule.minimumWorkMinutes || 0),
        max: Number(schedule.maximumWorkMinutes || 0),
      });
    }

    if (schedule.mode === 'split_shift') {
      return this.t('dailySchedulesCatalog.labels.splitShiftSummary', {
        count: schedule.blocks?.length || 0,
      });
    }

    if (schedule.mode === 'hours_only') {
      return this.t('dailySchedulesCatalog.labels.hoursOnly');
    }

    if (schedule.startTime && schedule.endTime) {
      return `${schedule.startTime} - ${schedule.endTime}`;
    }

    if (schedule.startTime) {
      return this.t('dailySchedulesCatalog.labels.startOnly', { start: schedule.startTime });
    }

    return this.t('dailySchedulesCatalog.labels.noRange');
  }

  private getMealBreakSummary(schedule: DailyScheduleConfig): string {
    if (!schedule.mealBreak.enabled) {
      return this.t('dailySchedulesCatalog.labels.noMealBreak');
    }

    return this.t('dailySchedulesCatalog.labels.mealBreakSummary', {
      mode: this.getMealBreakModeLabel(schedule.mealBreak.mode).toLowerCase(),
      minutes: Number(schedule.mealBreak.minutes || 0),
    });
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesRecordsResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private async loadJourneysForList(): Promise<JourneyConfig[]> {
    if (!this.authorizationService.hasAccess({ resource: 'journeys', action: 'read' })) {
      return [];
    }

    try {
      return await firstValueFrom(this.apiService.get<JourneyConfig[]>('labor-config/journeys')) || [];
    } catch (error) {
      console.warn('Could not load journey names for daily schedules', error);
      return [];
    }
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'dailySchedules', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
